<template>
	<li
		class="ReportListSpecies"
		:id="speciesListItem.speciesCode"
		:class="isMediaView ? 'ReportListSpecies--media' : ''"
		v-if="speciesIsVisible()"
	>
		<div class="ReportListSpecies-count">
			<span class="Heading Heading--h4 u-margin-none">
				<Count :count="speciesListItem.numIndividuals" :locale="locale" />
			</span>
		</div>
		<div class="ReportListSpecies-name">
			<CloHeading level="3" size="4">
				<CloSpecies
					:url="speciesListItem.category === 'species' ? `${baseUrl}species/${speciesListItem.speciesCode}` : ''"
					:commonName="speciesListItem.commonName"
					:sciName="speciesListItem.sciName"
					:sppPref="sppPref"
					:nonSpeciesTaxa="speciesListItem.category !== 'species'"
					:data-species-code="speciesListItem.category === 'species' ? speciesListItem.speciesCode : false"
				/>
			</CloHeading>
		</div>
		<!-- Lifer, exotic, sensitive, etc. -->
		<div
			class="ReportListSpecies-tags"
			v-if="
				speciesListItem.category === 'species' &&
				(speciesListItem.hideFlags === 'S' ||
					speciesListItem.isLifer ||
					speciesListItem.isPhotoLifer ||
					speciesListItem.isAudioLifer ||
					speciesListItem.exoticCategory)
			"
		>
			<!-- Exotic badge -->
			<span class="ReportListSpecies-tags-exotic" v-if="speciesListItem.exoticCategory" :title="$t(`exotic.categoryLabel`, [$t(`exotic.category${speciesListItem.exoticCategory}.title`)])" v-clo-tooltip >
				<ExoticBadge :exoticCategory="speciesListItem.exoticCategory" size="sm" />
			</span>
			<!-- Sensitive species badge -->
			<span class="ReportListSpecies-tags-sensitive" v-if="speciesListItem.hideFlags === 'S'">
				<CloBadge size="sm" state="alert">{{ $t('obs.sensitive') }}</CloBadge>
			</span>
			<!-- Species lifer badge -->
			<span
				v-if="speciesListItem.isLifer && speciesListItem.category === 'species'"
				class="ReportListSpecies-tags-lifer"
				:title="$t('data.speciesLifer')"
				v-clo-tooltip
			>
				<CloIcon symbol="badgeSeen" size="sm" />
			</span>
			<!-- Photo lifer badge -->
			<span
				v-if="speciesListItem.isPhotoLifer && speciesListItem.category === 'species'"
				class="ReportListSpecies-tags-lifer"
				:title="$t('data.photoLifer')"
				v-clo-tooltip
			>
				<CloIcon symbol="badgePhotographed" size="sm" />
			</span>
			<!-- Audio lifer badge -->
			<span
				v-if="speciesListItem.isAudioLifer && speciesListItem.category === 'species'"
				class="ReportListSpecies-tags-lifer"
				:title="$t('data.audioLifer')"
				v-clo-tooltip
			>
				<CloIcon symbol="badgeRecordedAudio" size="sm" />
			</span>
		</div>
		<!-- Meta summary -->
		<div v-if="!isPrintLayout && !isMediaView" class="ReportListSpecies-metaSummary">
			<button v-on:click="expanded = !expanded">
				<CloIcon v-show="!expanded" symbol="triangleRight" size="sm" />
				<CloIcon v-show="expanded" symbol="triangleDown" size="sm" />
				<div class="ReportListSpecies-metaSummary-items">
					<span v-if="speciesListItem.numMedia > 0" class="ReportListSpecies-metaSummary-items-item">
						<CloIcon symbol="figure" size="sm" />
						<span class="is-visuallyHidden">{{ $t('species.media') }}</span>
						<span>{{ speciesListItem.numMedia }}</span>
					</span>
					<span class="ReportListSpecies-metaSummary-items-item">
						<CloIcon symbol="checklist" size="sm" />
						<span class="is-visuallyHidden">{{ $t('generic.Checklists') }}</span>
						<span>{{ speciesListItem.numChecklists }}</span>
					</span>
				</div>
			</button>
		</div>
		<!-- Meta details -->
		<div
			v-if="expanded || view === 'photos' || view === 'audio'"
			class="ReportListSpecies-meta"
			v-clo-lazy-trigger:[this.speciesListItem.speciesCode]="lazyload"
		>
			<!-- Media -->
			<div class="ReportListSpecies-meta-media" v-if="!isPrintLayout && hasMediaToShow()">
				<CloResultsGallery
					v-if="hasMediaLoaded"
					:list="view === 'photos' ? photoAssets : view === 'audio' ? audioAssets : mediaAssets"
					dataFormat="search2.0"
					mediaVersion="2.0"
					:mediaDownloadUrl="mediaDownloadUrl"
                    :ebirdWebUrl="ebirdWebUrl"
                    :mediaSpecimenUrl="mediaSpecimenUrl"
					integratedLightbox
					ratingEnabled
					:getRatingsUrl="href_localRatingsServiceGet"
					:saveRatingUrl="href_localRatingsServicePost"
					:ratingCsrf="csrfToken"
					:rowHeightForSmall="180"
					:rowHeightForMedium="view === 'photos' ? 360 : 180"
					:animationSpeed="0.1"
					caption="checklist"
					:responsiveImages="false"
				/>
				<CloLoadingAnimation v-else />
			</div>
			<!-- Observations -->
			<template v-if="view === 'species'">
				<div v-if="showSensitiveDetails" class="ReportListSpecies-meta-obs">
					<ReportListSpeciesObs v-if="hasDetailsLoaded" :checklists="speciesDetails.checklists" :locale="locale" />
					<CloLoadingAnimation v-else />
				</div>
				<div v-else class="ReportListSpecies-meta-obs">
					<p class="u-text-2-loose u-inset-sm u-margin-none" v-html="$t('obs.sensitive.tip')"></p>
				</div>
			</template>
		</div>
	</li>
</template>

<script>
import axios from 'axios';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';
import CloImageAspectRatio from '@/media/CloImageAspectRatio/CloImageAspectRatio.vue';
import CloResultsGallery from '@/media/CloResultsGallery/CloResultsGallery';
import ReportListSpeciesObs from './ReportListSpeciesObs.vue';
import Count from './Count.vue';
import ExoticBadge from './ExoticBadge';
import CloBadge from '@/base/CloBadge/CloBadge.vue';
import CloSpecies from '@/base/CloSpecies/CloSpecies.vue';

export default {
	name: 'ReportListSpecies',
	components: {
		CloHeading,
		CloIcon,
		CloLoadingAnimation,
		CloImageAspectRatio,
		CloResultsGallery,
		ReportListSpeciesObs,
		Count,
		ExoticBadge,
		CloBadge,
		CloSpecies,
	},
	props: {
		speciesListItem: {
			type: Object,
			required: true,
		},
		tripReportId: {
			type: Number,
			required: true,
		},
		selectedPersonId: {
			type: String,
			default: '',
		},
		isMediaView: {
			type: Boolean,
			default: false,
		},
		isShowingAll: {
			type: Boolean,
			default: false,
		},
		view: {
			type: String,
			default: '',
		},
		locale: String,
		currentUser: Object,
		liferFilters: {
			type: Array,
			default() {
				return [];
			},
		},
		isPrintLayout: Boolean,
		sppPref: {
			type: String,
			default: 'both',
		},
		sppLocale: {
			type: String,
			required: true,
			default: 'en',
		},
	},
	watch: {
		isShowingAll() {
			this.expanded = this.isShowingAll;
		},
	},
	computed: {
		showSensitiveDetails() {
			if (this.speciesListItem.hideFlags === 'S') {
				if (this.currentUser.isParticipant) {
					return true;
				} else {
					return false;
				}
			} else {
				return true;
			}
		},
	},
	data() {
		return {
			baseUrl: window.baseUrl,
			tripApiBaseUrl: window.tripApiBaseUrl,
			contextRoot: window.contextRoot,
			mediaDownloadUrl: window.href_downloadService,
            mediaSpecimenUrl: window.mediaSpecimenUrl,
            ebirdWebUrl: window.ebirdWebUrl,
			href_localRatingsServiceGet: window.href_localRatingsServiceGet,
			href_localRatingsServicePost: window.href_localRatingsServicePost,
			csrfToken: window.csrfToken,
			expanded: this.isShowingAll,
			speciesDetails: [],
			obsDateFormatted: null,
			hasDetailsLoaded: false,
			hasMediaLoaded: false,
			mediaAssets: [],
			photoAssets: [],
			audioAssets: [],
			lazyTriggered: false,
			tempExoticCategory: '',
		};
	},
	mounted() {
		if (this.isPrintLayout) {
			this.expanded = true;
			this.lazyload(this.speciesListItem.speciesCode);
		}
	},
	methods: {
		lazyload(speciesCode) {
			// this extra check for expanded and view shouldn't be necessary
			// because it's already on the v-if for triggering lazyloading
			// but for some reason, this is still needed.
			if (this.expanded || this.view === 'photos' || this.view === 'audio') {
				if (!this.lazyTriggered) {
					this.lazyTriggered = true;
					this.getSpeciesDetails(speciesCode);
				}
			}
		},
		getSpeciesDetails(speciesCode) {
			const url = this.tripApiBaseUrl + 'taxon-detail/' + this.tripReportId + '/' + speciesCode;
			const params = {};
			if (this.selectedPersonId) {
				params.tripReportPersonId = this.selectedPersonId;
			}
			axios
				.get(url, { params: params })
				.then((res) => {
					// api returns null if no comments
					if (res.data) {
						this.speciesDetails = res.data;
						this.hasDetailsLoaded = true;
						if (this.speciesDetails.assetIds.length > 0) {
							this.getSpeciesAssets();
						}
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		getSpeciesAssets() {
			const params = {
				assetId: this.speciesDetails.assetIds.join(','),
				taxaLocale: this.sppLocale,
                count: this.speciesDetails.assetIds.length, // default count is 30 but could be a lot more
			};
			axios
				.get(this.contextRoot + '/ml-search-api/v2/search', { params: params })
				.then((res) => {
					this.mediaAssets = res.data.map((item) => {
						item.obsDtDisplay = this.localizeDate(item.obsDt);
						return item;
					});
					this.photoAssets = this.mediaAssets.filter((item) => item.mediaType === 'photo');
					this.audioAssets = this.mediaAssets.filter((item) => item.mediaType === 'audio');
					this.hasMediaLoaded = true;
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		hasMediaToShow() {
			if (this.view === 'photos') {
				return this.speciesListItem.numPhotos;
			}
			if (this.view === 'audio') {
				return this.speciesListItem.numAudio;
			}
			return this.speciesListItem.numMedia;
		},
		speciesIsVisible() {
			let visible = true;
			if (this.view === 'photos') {
				visible = this.speciesListItem.numPhotos;
			}
			if (this.view === 'audio') {
				visible = this.speciesListItem.numAudio;
			}
			// tricky: lifer filters should be combined with OR
			if (this.liferFilters.length) {
				visible = false;
				if (
					(this.liferFilters.includes('s') && this.speciesListItem.isLifer) ||
					(this.liferFilters.includes('p') && this.speciesListItem.isPhotoLifer) ||
					(this.liferFilters.includes('a') && this.speciesListItem.isAudioLifer)
				) {
					visible = true;
				}
			}
			return visible;
		},
		localizeDate(date) {
			const dateObj = new Date(date);
			const formattedDay = dateObj.toLocaleDateString(this.locale, { day: 'numeric' });
			const formattedMonth = dateObj.toLocaleDateString(this.locale, { month: 'short' });
			const formattedYear = dateObj.toLocaleDateString(this.locale, { year: 'numeric' });
			return `${formattedDay} ${formattedMonth} ${formattedYear}`;
		},
	},
};
</script>

<style lang="scss">
.ReportListSpecies {
	background-color: $color-white;
	padding: $space-sm $space-md;
	@include text(3);
	display: grid;
	grid-template-columns: auto auto 1fr auto;
	grid-column-gap: $space-sm;
	grid-row-gap: $space-sm;
	align-items: baseline;
	@include breakpoint(medium) {
		grid-column-gap: $space-md;
		padding: $space-sm $space-lg;
	}
}
.ReportListSpecies-count {
	grid-column: 1 / 2;
	grid-row: 1 / 2;
	background-color: $color-neutral-0;
	@include inset-squish-sm;
	border-radius: 3px;
}
.ReportListSpecies-name {
	grid-column: 2 / 3;
	grid-row: 1 / 2;
	// Remove unnecessary margin on species component inside heading component
	.Heading > .Species {
		margin-bottom: 0;
	}
}
.ReportListSpecies-tags {
	grid-column: 3 / 4;
	grid-row: 1 / 2;
	align-self: start;
	padding-top: $space-xs;
	display: flex;
	align-items: start;
	gap: $space-xs;
	.ReportListSpecies-tags-sensitive {
		.Badge {
			font-weight: normal;
			padding-top: 0.15rem;
		}
	}
	.ReportListSpecies-tags-lifer {
		// @include text(2);
		// @include inset-squish-sm;
		// background-color: $color-secondary-0;
		// border-radius: 16rem;
		min-height: 1.5rem;
		display: inline-flex;
		align-items: center;
		gap: $space-sm;
		color: var(--color-lifer, $color-primary-3);
	}
}

.ReportListSpecies-metaSummary {
	grid-column: 4 / 5;
	grid-row: 1 / 2;
	align-self: start;
	padding-top: $space-xs;
	display: flex;
	align-items: start;
	button {
		color: $color-secondary-3;
		display: flex;
		align-items: center;
		min-height: 1.5rem;
		.ReportListSpecies-metaSummary-items {
			display: flex;
			align-items: center;
			gap: $space-rel-xs;
			@include breakpoint(large) {
				gap: $space-rel-sm;
			}
		}
		.ReportListSpecies-metaSummary-items-item {
			display: flex;
			align-items: center;
			gap: 0.1em;
			@include breakpoint(large) {
				gap: $space-rel-xs;
			}
		}
		.Icon--triangleRight,
		.Icon--triangleDown {
			opacity: 0.5;
		}
		&:hover {
			.Icon--triangleRight,
			.Icon--triangleDown {
				opacity: 1;
			}
		}
	}
}
.ReportListSpecies-meta {
	grid-column: 1 / 5;
	grid-row: 2 / 3;
	background-color: $color-neutral-0;
	// min-height: 5rem;
	border-radius: $radius-sm;
	@include inset-md;
	margin: 0 -1rem -0.5rem;
	width: calc(100% + 2rem);
	@include breakpoint(medium) {
		width: 100%;
		@include stack-sm;
		@include inset-sm;
	}
	.LoadingAnimation {
		background-color: transparent;
		display: flex;
		align-items: center;
	}
}
.ReportListSpecies-meta-media {
	position: relative;
	@include stack-md;
	min-height: 9rem; // approx. height of one row of media
	.ResultsGallery .ResultsGallery-background {
		background-color: transparent;
	}
}
.ReportListSpecies-meta-obs {
	position: relative;
	min-height: 2.5rem; // approx. height of one row of obs
	ol {
		list-style: none;
		margin: 0;
		li {
			@include stack-sm;
		}
	}
}

// Variants
.ReportListSpecies--media {
	.ReportListSpecies-meta {
		background-color: transparent;
		padding-top: 0;
		padding-bottom: 0;
		@include breakpoint(medium) {
			margin: 0;
			padding: 0;
		}
	}
}

// cheat to hide unconfirmed badge
// because I think this is a bad idea,
// it's implemented temporarily here.
// To make permanent, need to add a prop to CloResultsGallery and CloLightbox
.ResultsGallery-caption .Icon--warning,
.Lightbox .Lightbox-caption-inner .Lightbox-unconfirmed {
	display: none;
}

.print {
	.ReportListSpecies {
		padding: $space-xs 0 0;
		@include stack-sm;
		border-top: 1pt solid $color-neutral-1;
		page-break-inside: avoid;
		display: flex;
		flex-wrap: wrap;
		row-gap: $space-xxs;
		page-break-inside: avoid;
	}
	.ReportListSpecies-count {
		background-color: transparent;
		padding: 0;
		.Count {
			min-width: $space-lg;
		}
	}
	.ReportListSpecies-name .Heading-main {
		color: $color-black;
		// page-break-inside: avoid;
	}
	.ReportListSpecies-tags {
		// page-break-after: avoid;
	}
	.ReportListSpecies-metaSummary {
		display: none;
		// page-break-after: avoid;
	}
	.ReportListSpecies-meta {
		padding: 0;
		background-color: transparent;
		grid-column: 2 / 5;
		margin-left: calc($space-lg + $space-md);
		margin-bottom: 0;
		width: 100%;
		max-width: 100%;
	}
	.ReportListSpecies-meta-media {
		@include stack-sm;
	}
	.ReportListSpecies-meta-obs {
		position: relative;
		min-height: auto;
		ol li {
			@include stack-xs;
		}
	}
	.ResultsGallery {
		page-break-inside: avoid;
	}
}
</style>
